import React, { useState, useRef } from 'react';
import { Camera, Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { uploadApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

interface ProfilePhotoUploadProps {
  currentPhotoUrl?: string;
  initials: string;
  onPhotoChange: (url: string | null) => void;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
}

const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  currentPhotoUrl,
  initials,
  onPhotoChange,
  size = 'lg',
  disabled = false,
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const sizeClasses = {
    sm: 'h-20 w-20',
    md: 'h-28 w-28',
    lg: 'h-36 w-36',
  };

  const buttonSizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10',
  };

  const iconSizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  const displayUrl = previewUrl || currentPhotoUrl;

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a JPG, PNG, GIF, or WebP image.',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 5MB.',
        variant: 'destructive',
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    setIsUploading(true);
    try {
      const result = await uploadApi.uploadProfilePhoto(file);
      onPhotoChange(result.url);
      toast({
        title: 'Photo uploaded',
        description: 'Profile photo has been updated successfully.',
      });
    } catch (error: any) {
      setPreviewUrl(null);
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload photo. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = () => {
    setPreviewUrl(null);
    onPhotoChange(null);
    toast({
      title: 'Photo removed',
      description: 'Profile photo has been removed.',
    });
  };

  const triggerFileInput = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative group">
        <div className="p-1.5 bg-white dark:bg-slate-900 rounded-3xl shadow-xl transition-all group-hover:shadow-2xl group-hover:scale-[1.02]">
          <Avatar className={`${sizeClasses[size]} rounded-2xl border-2 border-slate-50 dark:border-slate-800`}>
            <AvatarImage src={displayUrl || undefined} alt="Profile photo" className="object-cover" />
            <AvatarFallback className="bg-gradient-to-br from-blue-500 to-[#143A62] text-white text-3xl font-bold rounded-2xl">
              {initials}
            </AvatarFallback>
          </Avatar>
        </div>

        {/* Overlay on hover */}
        {!disabled && (
          <div
            className={`absolute inset-1.5 rounded-2xl bg-black/40 opacity-0 group-hover:opacity-100 transition-all cursor-pointer flex items-center justify-center ${sizeClasses[size]}`}
            onClick={triggerFileInput}
          >
            {isUploading ? (
              <Loader2 className={`${iconSizeClasses[size]} text-white animate-spin`} />
            ) : (
              <div className="flex flex-col items-center gap-1">
                <Camera className={`${iconSizeClasses[size]} text-white`} />
                <span className="text-[8px] font-bold text-white uppercase tracking-widest">Update</span>
              </div>
            )}
          </div>
        )}

        {/* Remove button */}
        {displayUrl && !disabled && !isUploading && (
          <button
            onClick={handleRemovePhoto}
            className={`absolute -top-2 -right-2 h-8 w-8 bg-white dark:bg-slate-800 text-red-500 rounded-xl flex items-center justify-center shadow-lg border border-slate-100 dark:border-slate-700 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all`}
            title="Remove photo"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || isUploading}
      />

      <div className="flex flex-col items-center gap-2">
        {!disabled && (
          <button
            type="button"
            onClick={triggerFileInput}
            disabled={isUploading}
            className="flex items-center gap-2 text-[#2563eb] dark:text-blue-400 text-[10px] font-bold uppercase tracking-widest hover:underline decoration-2 underline-offset-4"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Synchronizing...
              </>
            ) : (
              <>
                <Upload className="h-3 w-3" />
                {displayUrl ? 'Change Profile Photo' : 'Upload Profile Photo'}
              </>
            )}
          </button>
        )}
        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
          Max 5MB • JPG, PNG or WebP
        </p>
      </div>
    </div>
  );
};

export default ProfilePhotoUpload;
